import axios, { AxiosResponse } from "axios";
import { env } from "../config/env";
import {
  ACCESS_TOKEN_HEADER,
  BEARER_TOKEN_PREFIX,
  LOCAL_STORAGE_ACCESS_TOKEN_KEY,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY,
  REFRESH_TOKEN_HEADER,
} from "../constants/api.constants";
import {
  ApiPageRequest,
  ApiResponse,
  Controllers,
  PageResponse,
} from "../types/api.types";
import {
  AuthTokens,
  ForgotPasswordRequest,
  LoginCredentials,
  Permission,
} from "../types/auth.types";
import { Configuration } from "../types/config.types";
import {
  DeviceListFilters,
  DeviceListResponse,
  SellerDeviceListResponse,
} from "../types/device.types";
import {
  SalesInfo,
  SalesListRequest,
  SalesReport,
  TokenDistributionResponse,
} from "../types/sales.types";
import {
  SellerBasicInfo,
  SellerDashboardInfo,
  SellerFormData,
  SellerInfo,
  SellerSalesReportInfo,
} from "../types/seller.types";
const api = axios.create({
  baseURL: env.apiUrl,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
    if (!!token) {
      config.headers.Authorization = `${BEARER_TOKEN_PREFIX}${token}`;
    }
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
    if (!!refreshToken) {
      config.headers[REFRESH_TOKEN_HEADER] = refreshToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const headers = response.headers;
    const accessToken = headers[ACCESS_TOKEN_HEADER];
    if (!!accessToken) {
      localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, accessToken);
    }
    const refreshToken = headers[REFRESH_TOKEN_HEADER];
    if (!!refreshToken) {
      localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, refreshToken);
    }
    return response;
  },
  async (error) => {
    if (axios.isAxiosError(error)) {
      const originalRequest = error.config;
      
      // Prevent redirect loop for login page
      if (originalRequest?.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
        localStorage.removeItem("username");
        window.location.href = "/login";
      }
      if (error.response?.data?.message) {
        return Promise.reject(new Error(error.response.data.message));
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (
    credentials: LoginCredentials
  ): Promise<ApiResponse<AuthTokens>> => {
    try {
      const response = await api.post<ApiResponse<AuthTokens>>(
        `/${Controllers.AUTH}/login`,
        credentials
      );
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.data?.status || "ERROR";
        const message = error.response.data?.message || "Login failed";
        throw new Error(`${status}: ${message}`);
      }
      throw new Error(`Network error occurred: ${error.message}`);
    }
  },
  getUserPermissions: async (): Promise<ApiResponse<Permission[]>> => {
    try {
      const response = await api.get<ApiResponse<Permission[]>>(
        `/${Controllers.PERMISSION}/get-user-permissions`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to fetch permissions"
        );
      }
      throw new Error("Network error occurred");
    }
  },
  logout: () => {
    localStorage.clear();
  },
  getRoles: async (): Promise<ApiResponse<string[]>> => {
    try {
      const response = await api.get<ApiResponse<string[]>>(
        `/${Controllers.ROLE}/list`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || "Failed to fetch roles");
      }
      throw new Error("Network error occurred");
    }
  },
  generateOtp: async (request: { id: string }): Promise<ApiResponse<void>> => {
    const response = await api.post(`/${Controllers.AUTH}/otp`, request);
    return response.data;
  },
  forgotPassword: async (
    request: ForgotPasswordRequest
  ): Promise<ApiResponse<string>> => {
    const response = await api.post(
      `/${Controllers.AUTH}/forgot-password`,
      request
    );
    return response.data;
  },
  toggle2FA: async (enabled: boolean): Promise<ApiResponse<string>> => {
    const response = await api.post<ApiResponse<string>>(
      `/${Controllers.USER}/toggle-2fa`,
      { id: enabled }
    );
    return response.data;
  },
};

export const sellerApi = {
  create: async (
    formData: SellerFormData
  ): Promise<ApiResponse<SellerInfo>> => {
    try {
      const response = await api.post<ApiResponse<SellerInfo>>(
        `/${Controllers.SELLER}/create`,
        formData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || `Failed to create seller`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  update: async (
    formData: SellerFormData
  ): Promise<ApiResponse<SellerInfo>> => {
    try {
      const response = await api.put<ApiResponse<SellerInfo>>(
        `/${Controllers.SELLER}/update`,
        formData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || `Failed to update seller`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getSellers: async (): Promise<ApiResponse<SellerInfo[]>> => {
    try {
      const response = await api.get<ApiResponse<SellerInfo[]>>(
        `/${Controllers.SELLER}/list`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || `Failed to fetch sellers`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getById: async (id: string | number): Promise<ApiResponse<SellerInfo>> => {
    try {
      const response = await api.post<ApiResponse<SellerInfo>>(
        `/${Controllers.SELLER}/get-by-id`,
        { id }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || `Failed to fetch seller details`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  delete: async (
    id: string | number,
    sellerType: string
  ): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.SELLER}/delete`,
        { id, designation: sellerType.toUpperCase() }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || `Failed to delete ${sellerType}`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getSellerNames: async (): Promise<ApiResponse<SellerBasicInfo[]>> => {
    try {
      const response = await api.get<ApiResponse<SellerBasicInfo[]>>(
        `/${Controllers.SELLER}/names`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || `Failed to fetch seller names`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getCurrentUser: async (): Promise<ApiResponse<SellerInfo>> => {
    try {
      const response = await api.get<ApiResponse<SellerInfo>>(
        `/${Controllers.SELLER}/info`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to fetch user profile"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getByPhone: async (
    phone: string,
    sellerType: string
  ): Promise<ApiResponse<SellerInfo>> => {
    try {
      const response = await api.post<ApiResponse<SellerInfo>>(
        `/${Controllers.SELLER}/get-by-phone`,
        { phone, designation: sellerType.toUpperCase() }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message ||
            `Failed to fetch ${sellerType} by phone`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  updateProfile: async (request: any): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.SELLER}/update-profile`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to update profile"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  updateReferences: async (data: {
    referenceId: string;
    sellerIds: string[];
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post<ApiResponse<any>>(
        `/${Controllers.SELLER}/update-references`,
        data
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to update references"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  toggleUninstallConfirmation: async (
    sellerId: number,
    enabled: boolean
  ): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.SELLER}/toggle-uninstall-confirmation`,
        { sellerId, enabled }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message ||
            `Failed to toggle uninstall confirmation`
        );
      }
      throw new Error("Network error occurred");
    }
  },

  toggleDuePaymentNotification: async (
    sellerId: number,
    enabled: boolean
  ): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.SELLER}/toggle-due-payment-notification`,
        { sellerId, enabled }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message ||
            `Failed to toggle due payment notification`
        );
      }
      throw new Error("Network error occurred");
    }
  },
};

export const salesApi = {
  sellToken: async (formData: any): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post<ApiResponse<any>>(
        `/${Controllers.SALES}/sell-token`,
        formData
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || "Failed to sell tokens");
      }
      throw new Error("Network error occurred");
    }
  },

  getSalesList: async (
    request: ApiPageRequest<SalesListRequest>
  ): Promise<PageResponse<SalesInfo[]>> => {
    try {
      const response = await api.post<PageResponse<SalesInfo[]>>(
        `/${Controllers.SALES}/list`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to fetch sales list"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getSalesReport: async (params: {
    startDate: string;
    endDate: string;
  }): Promise<ApiResponse<SalesReport[]>> => {
    try {
      const response = await api.get<ApiResponse<SalesReport[]>>(
        `/${Controllers.SALES}/report`,
        { params }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to fetch sales report"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  approveSales: async (salesId: number): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.SALES}/approve-sales`,
        null,
        {
          params: { salesId },
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to approve sales"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  distributeToken: async (request: {
    receiverId: number;
    tokenAmount: number;
  }): Promise<ApiResponse<SellerInfo>> => {
    try {
      const response = await api.post<ApiResponse<SellerInfo>>(
        `/${Controllers.SALES}/distribute-token`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to distribute tokens"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  makeSalesPayment: async (request: {
    salesId: number;
    amount: number;
    paymentMethod: string;
    transactionId?: string;
  }): Promise<ApiResponse<SalesInfo>> => {
    try {
      const response = await api.post<ApiResponse<SalesInfo>>(
        `/${Controllers.SALES}/make-payment`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to make payment"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getTokenDistributionList: async (
    request: ApiPageRequest<TokenDistributionResponse>
  ): Promise<PageResponse<TokenDistributionResponse[]>> => {
    try {
      const response = await api.post<
        PageResponse<TokenDistributionResponse[]>
      >(`/${Controllers.SALES}/distribution-list`, request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message ||
            "Failed to fetch token distribution list"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  getTotalDueAmount: async (sellerId: number) => {
    try {
      const response = await api.post<ApiResponse<number>>(
        `/${Controllers.SALES}/due-amount`,
        { id: sellerId }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Network error");
      }
      throw new Error("Network error");
    }
  },

  paySellerDues: async (
    sellerId: number,
    amount: number
  ): Promise<ApiResponse<number>> => {
    try {
      const response = await api.post<ApiResponse<number>>(
        `/${Controllers.SALES}/pay-seller-dues`,
        { sellerId, amount }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || "Failed to apply payment");
      }
      throw new Error("Network error occurred");
    }
  },

  blockSoldDevices: async (request: {
    id: number;
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post<ApiResponse<any>>(
        `/${Controllers.SALES}/block-sold-devices`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to reverse sale"
        );
      }
      throw new Error("Network error occurred");
    }
  },
};

export const deviceApi = {
  registerDevice: async (data: any) => {
    const response = await api.post(`/${Controllers.DEVICE}/register`, data);
    return response.data;
  },
  getDevices: async (request: ApiPageRequest<DeviceListFilters>) => {
    const response = await api.post<PageResponse<DeviceListResponse[]>>(
      `/${Controllers.DEVICE}/list`,
      request
    );
    return response.data;
  },
  getDevice: async (deviceId: number) => {
    const response = await api.post(`/${Controllers.DEVICE}/get-by-id`, {
      id: deviceId,
    });
    return response.data;
  },
  updateDevice: async (deviceId: number, data: any) => {
    const response = await api.put(`/${Controllers.DEVICE}/update/${deviceId}`, data);
    return response.data;
  },
  releaseDevice: async (deviceId: number) => {
    const response = await api.post(`/${Controllers.DEVICE}/release`, {
      id: deviceId,
    });
    return response.data;
  },
  getDevicesBySeller: async (
    sellerId: string,
    request: ApiPageRequest<SellerDeviceListResponse>
  ): Promise<PageResponse<SellerDeviceListResponse>> => {
    try {
      const response = await api.post<PageResponse<SellerDeviceListResponse>>(
        `/${Controllers.DEVICE}/list-by-seller`,
        {
          ...request,
          filter: {
            ...request.filter,
            sellerId,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to fetch devices"
        );
      }
      throw new Error("Network error occurred");
    }
  },
  unlockDevices: async (request: {
    sellerId: string;
    deviceIds: string[];
  }): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.DEVICE}/force-unlock-devices`,
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to unlock devices"
        );
      }
      throw new Error("Network error occurred");
    }
  },

  toggleReminderActive: async (
    deviceId: string,
    enabled: boolean
  ): Promise<ApiResponse<string>> => {
    try {
      const response = await api.post<ApiResponse<string>>(
        `/${Controllers.DEVICE}/toggle-reminder-active`,
        { deviceId, enabled }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to toggle auto EMI reminder"
        );
      }
      throw new Error("Network error occurred");
    }
  },
};

export const configApi = {
  getAll: async (): Promise<ApiResponse<Configuration[]>> => {
    try {
      const response: AxiosResponse<ApiResponse<Configuration[]>> =
        await api.post(`/${Controllers.CONFIG}/all`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch configurations");
    }
  },

  getContacts: async (): Promise<ApiResponse<Configuration[]>> => {
    try {
      const response = await api.get<ApiResponse<Configuration[]>>(
        `/${Controllers.CONTACT}/contacts`
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch contacts configuration");
    }
  },

  set: async (request: Configuration): Promise<ApiResponse<Configuration>> => {
    try {
      const response: AxiosResponse<ApiResponse<Configuration>> =
        await api.post(`/${Controllers.CONFIG}/set`, request);
      return response.data;
    } catch (error) {
      throw new Error("Failed to set configuration");
    }
  },

  getByKey: async (key: string): Promise<ApiResponse<Configuration>> => {
    try {
      const response = await api.post<ApiResponse<Configuration>>(
        `/${Controllers.CONFIG}/get`,
        { id: key }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message ||
            `Failed to fetch configuration for key: ${key}`
        );
      }
      throw new Error("Network error occurred");
    }
  },
};

export const stakeholderApi = {
  getCurrentUser: async (): Promise<ApiResponse<SellerInfo>> => {
    return sellerApi.getCurrentUser();
  },
};

export const dashboardApi = {
  getSellerDashboardInfo: async (): Promise<
    ApiResponse<SellerDashboardInfo>
  > => {
    try {
      const response = await api.get<ApiResponse<SellerDashboardInfo>>(
        `/${Controllers.DASHBOARD}/seller-dashboard-info`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          error.response.data.message || "Failed to fetch dashboard info"
        );
      }
      throw new Error("Network error occurred");
    }
  },

};

export const contactApi = {
  send: async (payload: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<ApiResponse<string>> => {
    const response = await api.post<ApiResponse<string>>(
      `/${Controllers.CONTACT}/send`,
      payload
    );
    return response.data;
  },
};

export default api;
